import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, getPushState, subscribePush, sendTestPush, isIOS, isStandalone } from "@/lib/push";

const DISMISS_KEY = "uaic_pushprompt_dismissed";
const DAYS = 7;

// Barra amable que invita a activar las notificaciones del sistema (barra del celular),
// una sola vez tras iniciar sesión. Si ya están activas o se descartó hace poco, no aparece.
export default function PushPrompt({ parentToken = null }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!pushSupported()) {
        // iOS solo permite push con la app instalada en inicio.
        if (isIOS() && !isStandalone() && !dismissed()) { if (alive) setShow(true); }
        return;
      }
      try {
        const st = await getPushState(parentToken);
        if (alive && st !== "subscribed" && st !== "denied" && !dismissed()) setShow(true);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [parentToken]);

  const dismissed = () => {
    try {
      const v = localStorage.getItem(DISMISS_KEY);
      return v && Date.now() - Number(v) < DAYS * 86400000;
    } catch { return false; }
  };
  const close = () => { try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ } setShow(false); };

  const enable = async () => {
    if (!pushSupported()) {
      if (isIOS() && !isStandalone()) {
        toast.info("En iPhone/iPad: toca Compartir → \u201CA\u00f1adir a inicio\u201D. Abre la app desde el \u00edcono y ah\u00ed activa las notificaciones.", { duration: 9000 });
      }
      return;
    }
    setBusy(true);
    try {
      await subscribePush(parentToken);
      toast.success("Notificaciones activadas");
      try { await sendTestPush(parentToken); } catch { /* noop */ }
      close();
    } catch (e) {
      if (e.message === "denied") toast.error("Bloqueaste las notificaciones. Actívalas en los ajustes del navegador.");
      else toast.error("No se pudieron activar las notificaciones.");
    } finally { setBusy(false); }
  };

  if (!show) return null;

  return (
    <div data-testid="push-prompt" className="mb-4 rounded-xl border border-navy/20 bg-navy/5 px-4 py-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-navy text-white flex items-center justify-center shrink-0">
        <Bell className="w-4.5 h-4.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-navy leading-tight">Activa las notificaciones</div>
        <div className="text-[12px] text-nardo-600 leading-snug">Recibe avisos y tareas al instante en tu celular o computadora, aunque la app esté cerrada.</div>
      </div>
      <button onClick={enable} disabled={busy} data-testid="push-prompt-enable" className="shrink-0 inline-flex items-center gap-1.5 bg-navy text-white text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-navy-light transition-colors disabled:opacity-60">
        <Bell className="w-3.5 h-3.5" /> {busy ? "Activando…" : "Activar"}
      </button>
      <button onClick={close} data-testid="push-prompt-close" className="shrink-0 text-nardo-400 hover:text-navy p-1" title="Ahora no"><X className="w-4 h-4" /></button>
    </div>
  );
}
